import { t } from 'i18next';

import i18n from 'i18n/i18n';
import { addressContext } from 'features/conversation-preferences/address-form';
import type { AddressForm } from 'features/conversation-preferences/services/conversation-preferences-service';

/** Area chip options: translation key + English fallback. */
export const ONBOARDING_TEAM_OPTIONS = [
  { key: 'ONBOARDING_TEAM_CONSULTANCY', defaultValue: 'Consulting' },
  { key: 'ONBOARDING_TEAM_MARKETING', defaultValue: 'Marketing' },
  { key: 'ONBOARDING_TEAM_SALES', defaultValue: 'Sales' },
  { key: 'ONBOARDING_TEAM_HR', defaultValue: 'HR' },
  { key: 'ONBOARDING_TEAM_FINANCE', defaultValue: 'Finance' },
  { key: 'ONBOARDING_TEAM_CUSTOMER_SUPPORT', defaultValue: 'Customer support' },
  { key: 'ONBOARDING_TEAM_IT', defaultValue: 'IT' },
  { key: 'ONBOARDING_TEAM_EXECUTIVE_FOUNDER', defaultValue: 'Management' },
  { key: 'ONBOARDING_TEAM_DESIGN', defaultValue: 'Design' },
] as const;

/** Tool chip options: translation key + English fallback. Product names are never translated. */
export const ONBOARDING_TOOL_OPTIONS = [
  { key: 'ONBOARDING_TOOL_OUTLOOK', defaultValue: 'Outlook' },
  { key: 'ONBOARDING_TOOL_MICROSOFT_TEAMS', defaultValue: 'Microsoft Teams' },
  { key: 'ONBOARDING_TOOL_EXCEL', defaultValue: 'Excel' },
  { key: 'ONBOARDING_TOOL_WORDS', defaultValue: 'Word' },
  { key: 'ONBOARDING_TOOL_ONE_DRIVE', defaultValue: 'OneDrive' },
  { key: 'ONBOARDING_TOOL_GOOGLE_ANALYTICS', defaultValue: 'Google Analytics' },
  { key: 'ONBOARDING_TOOL_HUBSPOT', defaultValue: 'HubSpot' },
  { key: 'ONBOARDING_TOOL_GOOGLE_DOCS', defaultValue: 'Google Docs' },
  { key: 'ONBOARDING_TOOL_SLACK', defaultValue: 'Slack' },
  { key: 'ONBOARDING_TOOL_GOOGLE_SHEETS', defaultValue: 'Google Sheets' },
] as const;

export const ONBOARDING_ADDRESS_OPTIONS = [
  { value: 'informal', key: 'ONBOARDING_ADDRESS_INFORMAL', defaultValue: 'Informal' },
  { value: 'formal', key: 'ONBOARDING_ADDRESS_FORMAL', defaultValue: 'Formal' },
] as const;

export const ONBOARDING_CONVERSATION_PREFIX = 'onboarding-';

/** The UI language the answers and Buddy's lines are in. */
export const onboardingLanguage = () => i18n.language || 'en-US';

export function isOnboardingConversationId(id?: string | null): boolean {
  return Boolean(id?.startsWith(ONBOARDING_CONVERSATION_PREFIX));
}

export function formatOnboardingName(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

const LIST_CONJUNCTIONS: Record<string, string> = { de: 'und', fr: 'et', it: 'e' };

/** "Outlook, Excel and HubSpot", with the conjunction of the UI language. */
export function formatOnboardingList(items: string[], language = onboardingLanguage()): string {
  const labels = items.map((item) => item.trim()).filter(Boolean);
  if (labels.length <= 1) return labels[0] ?? '';
  const conjunction = LIST_CONJUNCTIONS[language.split('-')[0].toLowerCase()] ?? 'and';
  return `${labels.slice(0, -1).join(', ')} ${conjunction} ${labels[labels.length - 1]}`;
}

/** Asks for the name and, in German, French and Italian, whether to use du. */
export function onboardingNameQuestion(): string {
  return t('ONBOARDING_NAME_QUESTION', {
    defaultValue: "Hi, I'm Buddy! What should I call you?",
  });
}

export function onboardingNameInvalid(form?: AddressForm): string {
  return t('ONBOARDING_NAME_VALIDATION_INVALID', {
    defaultValue: 'What should I call you? Just type your name, for example "Luca".',
    context: addressContext(form),
  });
}

export function onboardingAnswerUnrelated(form?: AddressForm): string {
  return t('ONBOARDING_ANSWER_UNRELATED', {
    defaultValue: "I didn't quite get that.",
    context: addressContext(form),
  });
}

export function onboardingAnswerRetry(form?: AddressForm): string {
  return t('ONBOARDING_ANSWER_RETRY', {
    defaultValue: "Hang on, something's stuck. Please try again in a moment.",
    context: addressContext(form),
  });
}

/** Salutation questions are asked in formal mode only, so they have no informal variant. */
export function onboardingSalutationQuestion(): string {
  return t('ONBOARDING_SALUTATION_QUESTION', { defaultValue: 'How should I address you?' });
}

export function onboardingSalutationCustomPrompt(): string {
  return t('ONBOARDING_SALUTATION_CUSTOM_PROMPT', {
    defaultValue: 'How should I address you? Just type it, for example Dr. Müller.',
  });
}

/** `name` is the given name (du, English); `salutationName` how Sie mode addresses the user. */
export function onboardingIndustryQuestion({
  form,
  name,
  salutationName,
}: { form?: AddressForm; name?: string; salutationName?: string } = {}): string {
  const given = name?.trim();
  if (!given && !salutationName) {
    return t('ONBOARDING_INDUSTRY_QUESTION', {
      defaultValue: 'Which industry do you work in?',
      context: addressContext(form),
    });
  }
  return t('ONBOARDING_INDUSTRY_QUESTION_NAMED', {
    defaultValue: 'Nice to meet you, {{name}}! Which industry do you work in?',
    context: addressContext(form),
    name: given || salutationName,
    salutationName: salutationName || given,
  });
}

export function onboardingIndustryCustomPrompt(form?: AddressForm): string {
  return t('ONBOARDING_INDUSTRY_CUSTOM_PROMPT', {
    defaultValue: 'Just type your industry.',
    context: addressContext(form),
  });
}

export function onboardingTeamQuestion(industry?: string, form?: AddressForm): string {
  const trimmed = industry?.trim();
  if (!trimmed) {
    return t('ONBOARDING_TEAM_QUESTION', {
      defaultValue: 'Which area do you work in?',
      context: addressContext(form),
    });
  }
  return t('ONBOARDING_TEAM_QUESTION_INDUSTRY', {
    defaultValue: '{{industry}}, interesting! Which area do you work in?',
    context: addressContext(form),
    industry: trimmed,
  });
}

export function onboardingToolsQuestion(team?: string, form?: AddressForm): string {
  const trimmed = team?.trim();
  if (!trimmed) {
    return t('ONBOARDING_TOOLS_QUESTION', {
      defaultValue: 'Which programs do you use day to day? You can pick several.',
      context: addressContext(form),
    });
  }
  return t('ONBOARDING_TOOLS_QUESTION_TEAM', {
    defaultValue: '{{team}}, got it. Which programs do you use day to day? You can pick several.',
    context: addressContext(form),
    team: trimmed,
  });
}

export function onboardingToolsAcknowledged(tools: string, form?: AddressForm): string {
  return t('ONBOARDING_TOOLS_ACKNOWLEDGED', {
    defaultValue: '{{tools}}, great. That helps me help you where it counts.',
    context: addressContext(form),
    tools: tools.trim(),
  });
}

export function onboardingCompleteMessage(form?: AddressForm): string {
  return t('ONBOARDING_COMPLETE_MESSAGE', {
    defaultValue: 'All set! With Klax, your work becomes a breeze.',
    context: addressContext(form),
  });
}

/** Catch-up for users who finished the old onboarding; the form is still unknown here. */
export function onboardingCatchUpIntro(): string {
  return t('ONBOARDING_CATCHUP_INTRO', {
    defaultValue: 'Welcome back! Two quick questions so I get to know you better.',
  });
}

/** The written starter prompt, in the user's voice and mirroring their form toward Buddy. */
export function onboardingStartCreatingPrompt(
  answers: {
    onboardingTeam?: string;
    onboardingTools?: string;
    onboardingIndustry?: string;
  } = {},
  form?: AddressForm
): string {
  const clean = (value: string | undefined, limit: number) =>
    [...(value ?? '')]
      .filter(
        (character) =>
          character.charCodeAt(0) >= 32 &&
          character.charCodeAt(0) !== 127 &&
          !/[<>`]/.test(character)
      )
      .join('')
      .trim()
      .slice(0, limit);
  const team = clean(answers.onboardingTeam, 120);
  const tools = clean(answers.onboardingTools, 360);
  const industry = clean(answers.onboardingIndustry, 80);
  const context =
    team && tools && industry
      ? t('ONBOARDING_STARTER_CONTEXT', {
          defaultValue: 'I work in {{team}} in the {{industry}} industry and use {{tools}}.',
          team,
          industry,
          tools,
        })
      : '';
  // `{{context}}` is the sentence above, so i18next's own `context` option can't pick the du/Sie
  // variant here; the informal key comes first and falls back to the formal one.
  const keys =
    addressContext(form) === 'informal'
      ? ['ONBOARDING_STARTER_PROMPT_informal', 'ONBOARDING_STARTER_PROMPT']
      : 'ONBOARDING_STARTER_PROMPT';
  return t(keys, {
    defaultValue:
      '{{context}} Show me with one concrete task how you can save me time. First ask me which task takes up most of my time right now.',
    context,
  }).trim();
}
